import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  CheckCircle,
  Receipt,
  CreditCard,
  Hammer,
  FileText,
  Image as ImageIcon,
} from "@phosphor-icons/react";
import { PortalShell } from "@/components/PortalShell";
import { PortalLightbox } from "@/components/PortalLightbox";
import { PortalMessages } from "@/components/PortalMessages";
import { PortalSlabLayout } from "@/components/PortalSlabLayout";
import { DesignerPlan } from "@/components/CountertopDesigner";
import { SavedCutList } from "@/components/portal/SavedCutList";
import { Ruler } from "@phosphor-icons/react";
import {
  portalApi,
  portalAsset,
  portalMoney,
  portalDate,
} from "@/lib/portalApi";

const STAGES = [
  "Lead",
  "Quoted",
  "Approved",
  "Template",
  "Fabrication",
  "Install",
  "Complete",
];

const DateRow = ({ label, date, window }) => (
  <div className="flex items-baseline justify-between gap-3 border-b border-slate-200 py-2.5 text-sm last:border-0">
    <span className="text-slate-500">{label}</span>
    <span className="font-semibold text-slate-900">
      {date ? `${portalDate(date)}${window ? ` · ${window}` : ""}` : "To book"}
    </span>
  </div>
);

const Panel = ({ title, sub, icon: Icon, children, testid }) => (
  <section
    className="rounded-2xl border border-slate-200/80 bg-white p-5"
    data-testid={testid}
  >
    <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900 md:text-lg">
      {Icon && <Icon size={18} weight="bold" className="text-slate-500" />}
      {title}
    </h2>
    {sub && <p className="mt-1 text-sm text-slate-500">{sub}</p>}
    <div className="mt-4">{children}</div>
  </section>
);

export default function PortalProject() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [galleryIdx, setGalleryIdx] = useState(-1);
  const [light, setLight] = useState(-1);
  const [cutCount, setCutCount] = useState(0);

  const gallery = useMemo(() => {
    const f = (p?.fixtures || [])[galleryIdx];
    const photos = f?.photos || [];
    return photos.map((ph, n) => ({
      path: ph.path,
      thumb: ph.thumb,
      title: f?.sink || "Sink",
      sub: `${f?.area || ""}${photos.length > 1 ? ` · photo ${n + 1} of ${photos.length}` : ""}`,
    }));
  }, [p, galleryIdx]);

  const load = useCallback(async () => {
    const { data } = await portalApi.get(`/projects/${id}`);
    setP(data);
    try {
      const res = await portalApi.get("/invoices");
      setInvoices(Array.isArray(res.data) ? res.data : []);
    } catch {
      setInvoices([]);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (!p)
    return (
      <PortalShell back="/portal/home">
        <div className="mt-10 h-10 w-10 animate-pulse rounded-full bg-slate-100" />
      </PortalShell>
    );

  const reached = Math.max(STAGES.indexOf(p.stage), 0);
  const job = p.job;
  const myInvoices = invoices.filter(
    (i) =>
      i.project_id === p.id ||
      (p.quotes || []).some((q) => q.id === i.quote_id),
  );
  const owing = myInvoices.reduce(
    (n, i) => n + (i.status === "Void" ? 0 : Number(i.balance || 0)),
    0,
  );

  return (
    <PortalShell
      back="/portal/home"
      title={p.name}
      subtitle={[p.code, p.job_address].filter(Boolean).join(" · ")}
    >
      <div
        className="mt-6 grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_360px]"
        data-testid="portal-project-page"
      >
        <div className="min-w-0 space-y-6">
          <Panel title="Where your project is" testid="portal-project-progress">
            <div className="flex flex-wrap items-center gap-2">
              {STAGES.map((s, i) => (
                <div key={s} className="flex items-center gap-2">
                  <span
                    data-testid={`portal-project-stage-${s.toLowerCase()}`}
                    className={`rounded-full px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-widest ${
                      i <= reached
                        ? "bg-[#c39e6d] text-white"
                        : "bg-slate-100 text-slate-400"
                    }`}
                  >
                    {s}
                  </span>
                  {i < STAGES.length - 1 && (
                    <span className="h-px w-4 bg-slate-100" />
                  )}
                </div>
              ))}
            </div>
            {p.accepted_total !== null && p.accepted_total !== undefined && (
              <p className="mt-4 text-sm text-slate-500">
                Accepted work:{" "}
                <strong className="font-mono-data">
                  {portalMoney(p.accepted_total)}
                </strong>
                {p.sqft > 0 && ` · ${p.sqft} ft²`}
              </p>
            )}
            {job && p.visibility?.show_job_dates !== false && (
              <div className="mt-4">
                <DateRow
                  label="Template"
                  date={job.template_date}
                  window={job.template_window}
                />
                <DateRow
                  label="Fabrication"
                  date={job.fab_date}
                  window={job.fab_window}
                />
                <DateRow
                  label="Install"
                  date={job.install_date}
                  window={job.install_window}
                />
              </div>
            )}
          </Panel>

          {(p.fixtures || []).length > 0 && (
            <Panel
              title="Your sinks & materials"
              icon={Hammer}
              sub="What we've set aside for your job."
              testid="portal-project-materials"
            >
              <div className="space-y-2">
                {p.fixtures.map((f, i) => (
                  <div
                    key={i}
                    data-testid={`portal-project-material-${i}`}
                    className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 p-3.5"
                  >
                    {f.photos?.length || f.photo ? (
                      <button
                        type="button"
                        data-testid={`portal-project-material-photo-${i}`}
                        onClick={() => {
                          if (f.photos?.length) {
                            setGalleryIdx(i);
                            setLight(0);
                          }
                        }}
                        className="relative h-12 w-12 shrink-0 cursor-zoom-in overflow-hidden rounded-lg border border-slate-200"
                      >
                        <img
                          src={portalAsset(
                            `/inventory/photo/${f.photos?.[0]?.thumb || f.photos?.[0]?.path || f.photo}?w=200`,
                          )}
                          alt={f.sink || "Sink"}
                          className="h-full w-full object-cover"
                        />
                        {f.photos?.length > 1 && (
                          <span className="absolute bottom-0 right-0 rounded-tl bg-slate-900/70 px-1 text-[9px] font-bold text-white">
                            {f.photos.length}
                          </span>
                        )}
                      </button>
                    ) : null}
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-slate-900">
                        {f.sink || "Sink"}
                      </span>
                      {f.area && (
                        <span className="block text-xs text-slate-500">
                          {f.area}
                          {f.mount ? ` · ${f.mount}` : ""}
                        </span>
                      )}
                    </span>
                    {f.reserved && (
                      <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-emerald-700">
                        Reserved
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </Panel>
          )}
          {light >= 0 && gallery.length > 0 && (
            <PortalLightbox
              shots={gallery}
              index={light}
              srcOf={(path) => portalAsset(`/inventory/photo/${path}`)}
              onClose={() => setLight(-1)}
              prefix="portal-material-lightbox"
            />
          )}
          <div className={cutCount > 0 ? "" : "hidden"}>
            <Panel
              title="Saved cuts for this project"
              icon={Ruler}
              sub="Pieces you sized on our slabs — ask us to quote one any time."
              testid="portal-project-saved-cuts"
            >
              <SavedCutList projectId={p.id} emptyHint={false} onCount={setCutCount} />
            </Panel>
          </div>
          <Panel
            title="Your quotes"
            icon={Receipt}
            sub="Open a quote to see the full breakdown, print it or approve it."
            testid="portal-project-quotes"
          >
            {p.quotes.length === 0 && (
              <p className="text-sm text-slate-400">No quotes yet.</p>
            )}
            <div className="space-y-2">
              {p.quotes.map((q) => (
                <Link
                  key={q.id}
                  to={`/portal/quotes/${q.id}`}
                  data-testid={`portal-project-quote-${q.id}`}
                  className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 p-3.5 transition-colors duration-150 hover:border-slate-300"
                >
                  <span className="font-mono-data text-xs text-slate-500">
                    {q.code}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900">
                    {q.project_name}
                  </span>
                  {q.accepted_at ? (
                    <span className="flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-emerald-800">
                      <CheckCircle size={11} weight="bold" /> Accepted
                    </span>
                  ) : (
                    <span className="rounded-full bg-blue-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-blue-800">
                      {q.status}
                    </span>
                  )}
                  {q.total !== null && q.total !== undefined && (
                    <span className="font-mono-data text-sm font-bold text-slate-900">
                      {portalMoney(q.total)}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          </Panel>

          {myInvoices.length > 0 && (
            <Panel
              title="Your invoices"
              icon={CreditCard}
              sub={
                owing > 0
                  ? `${portalMoney(owing)} still to pay — open an invoice to pay by card or print a copy.`
                  : "Everything on this project is paid. Thank you."
              }
              testid="portal-project-invoices"
            >
              <div className="space-y-2">
                {myInvoices.map((i) => (
                  <Link
                    key={i.id}
                    to={`/portal/invoices/${i.id}`}
                    data-testid={`portal-project-invoice-${i.id}`}
                    className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 p-3.5 transition-colors duration-150 hover:border-slate-300"
                  >
                    <span className="font-mono-data text-xs text-slate-500">
                      {i.code}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900">
                      {i.title}
                    </span>
                    <span className="text-xs text-slate-500">
                      {i.due_date
                        ? `Due ${portalDate(i.due_date)}`
                        : "Due on install"}
                    </span>
                    <span
                      className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${
                        i.status === "Paid"
                          ? "bg-emerald-50 text-emerald-700"
                          : i.status === "Partly paid"
                            ? "bg-amber-100 text-amber-800"
                            : "bg-teal-50 text-teal-700"
                      }`}
                    >
                      {i.status}
                    </span>
                    <span className="font-mono-data text-sm font-bold text-slate-900">
                      {portalMoney(i.balance)}
                    </span>
                  </Link>
                ))}
              </div>
            </Panel>
          )}

          {p.visibility?.show_drawing !== false &&
            (p.drawing_areas || []).length > 0 && (
              <Panel
                title="Shop drawing"
                sub="Every side, edge profile and cut-out we will fabricate."
                testid="portal-project-drawing"
              >
                {p.drawing_areas.map((a) => (
                  <div key={a.id} className="mb-5">
                    <div className="text-sm font-semibold text-slate-900">
                      {a.name}
                    </div>
                    <div className="mt-2 border border-slate-200 p-3">
                      <DesignerPlan
                        pieces={a.pieces || []}
                        hideCutoutDims={p.hide_cutout_dims !== false}
                      />
                    </div>
                  </div>
                ))}
              </Panel>
            )}

          {p.visibility?.show_layout !== false &&
            (p.layout?.slabs || []).length > 0 && (
              <Panel
                title="Your slab layout"
                sub="How your pieces are nested on the slab, so you can see where the vein lands."
                testid="portal-project-layout"
              >
                <PortalSlabLayout
                  quote={{
                    layout: p.layout,
                    areas: p.drawing_areas,
                    slab_color: "",
                  }}
                />
              </Panel>
            )}

          {job && (job.photos || []).length > 0 && (
            <Panel
              title="Photos"
              icon={ImageIcon}
              testid="portal-project-photos"
            >
              <div className="grid gap-3 sm:grid-cols-3">
                {job.photos.map((ph) => (
                  <figure
                    key={ph.id}
                    data-testid={`portal-project-photo-${ph.id}`}
                  >
                    <img
                      src={portalAsset(`/jobs/${job.id}/photos/${ph.id}`)}
                      alt={ph.caption || "Job photo"}
                      className="h-44 w-full rounded-xl object-cover"
                    />
                    {ph.caption && (
                      <figcaption className="mt-1.5 text-xs text-slate-500">
                        {ph.caption}
                      </figcaption>
                    )}
                  </figure>
                ))}
              </div>
            </Panel>
          )}

          {job && (job.documents || []).length > 0 && (
            <Panel
              title="Documents"
              icon={FileText}
              testid="portal-project-documents"
            >
              <div className="divide-y divide-slate-200">
                {job.documents.map((d) => (
                  <a
                    key={d.id}
                    href={portalAsset(`/jobs/${job.id}/files/${d.id}`)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`portal-project-document-${d.id}`}
                    className="flex items-center justify-between gap-3 py-3 text-sm text-slate-700 hover:text-slate-900"
                  >
                    <span className="truncate">{d.name}</span>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                      Open
                    </span>
                  </a>
                ))}
              </div>
            </Panel>
          )}

          {job && (
            <Link
              to={`/portal/jobs/${job.id}`}
              data-testid="portal-project-job-link"
              className="flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900"
            >
              <Hammer size={16} weight="bold" /> Open the full job page
            </Link>
          )}
        </div>

        {p.visibility?.allow_notes !== false && (
          <aside
            className="lg:sticky lg:top-6"
            data-testid="portal-notes-panel"
          >
            <PortalMessages subjectType="project" subjectId={p.id} />
          </aside>
        )}
      </div>
    </PortalShell>
  );
}
